// maths.portll.net — account & family settings.
//
// The trusted-plane writers live in Cloud Functions; this page is the polite front
// door to them: set the grown-up PIN + per-child PINs, (optionally) link Apple, review
// a dates-only "what we've done at home" record (NO scores, ever), sign out, and the
// store-compliant account erasure. Everything destructive is behind a typed confirm.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext.jsx";
import { useProfiles } from "../contexts/ProfileContext.jsx";
import {
  callSetParentPin,
  callSetChildPin,
  callClearChildPin,
  callDeleteAccount,
  linkApple,
} from "../lib/firebase.js";
import { subscribeCompletions, yearLabel } from "../lib/tasks.js";
import {
  PageHeader,
  Card,
  Chip,
  Button,
  Field,
  Note,
  Spinner,
  EmptyState,
  injectStyles,
} from "../components/ui.jsx";
import { Avatar } from "../components/AvatarPicker.jsx";
import { useThemePref } from "../lib/theme.js";
import { useVoicePref, VOICES } from "../lib/voice.js";

const APPLE_ENABLED =
  import.meta.env.VITE_APPLE_SIGN_IN_ENABLED === "1" ||
  import.meta.env.VITE_APPLE_SIGN_IN_ENABLED === "true";

const isPin = (s) => /^[0-9]{4}$/.test(s);
const errText = (e) => (e && e.message) || "Something went wrong. Please try again.";

function PinInput({ id, value, onChange, placeholder = "••••" }) {
  return (
    <input
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="off"
      maxLength={4}
      placeholder={placeholder}
      style={{ maxWidth: "8rem", letterSpacing: ".4em", textAlign: "center" }}
    />
  );
}

// ── appearance (light / dark / system) ──────────────────────────────────────────
const THEME_OPTIONS = [
  { value: "system", label: "System", icon: "🖥️" },
  { value: "light", label: "Light", icon: "☀️" },
  { value: "dark", label: "Dark", icon: "🌙" },
];

function AppearanceSection() {
  const [choice, setChoice] = useThemePref();
  return (
    <Card className="set-section">
      <h2 className="set-section__title">Appearance</h2>
      <p className="set-section__lead">
        Choose a light or dark look for the app and the game. “System” follows your device setting.
      </p>
      <div className="set-theme" role="group" aria-label="Colour theme">
        {THEME_OPTIONS.map((o) => {
          const active = choice === o.value;
          return (
            <Button
              key={o.value}
              variant={active ? "primary" : "secondary"}
              size="sm"
              aria-pressed={active}
              onClick={() => setChoice(o.value)}
            >
              <span aria-hidden="true">{o.icon}</span> {o.label}
            </Button>
          );
        })}
      </div>
    </Card>
  );
}

// ── narration voice (Leah / Tara / Sam) ─────────────────────────────────────────
function VoiceSection() {
  const [voice, setChoice] = useVoicePref();
  return (
    <Card className="set-section">
      <h2 className="set-section__title">Narration voice</h2>
      <p className="set-section__lead">
        The voice that reads numbers and prompts aloud in the game. Takes effect next time a game opens.
      </p>
      <div className="set-theme" role="group" aria-label="Narration voice">
        {VOICES.map((o) => {
          const active = voice === o.value;
          return (
            <Button
              key={o.value}
              variant={active ? "primary" : "secondary"}
              size="sm"
              aria-pressed={active}
              onClick={() => setChoice(o.value)}
            >
              <span aria-hidden="true">🔊</span> {o.label}
            </Button>
          );
        })}
      </div>
    </Card>
  );
}

// ── grown-up PIN ──────────────────────────────────────────────────────────────
function ParentPinSection() {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  async function save() {
    if (!isPin(pin)) return setMsg({ tone: "danger", text: "PIN must be exactly 4 digits." });
    if (pin !== confirm) return setMsg({ tone: "danger", text: "The two PINs don't match." });
    setBusy(true);
    setMsg(null);
    try {
      await callSetParentPin({ pin });
      setPin("");
      setConfirm("");
      setMsg({ tone: "ok", text: "Grown-up PIN saved." });
    } catch (e) {
      setMsg({ tone: "danger", text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="set-section">
      <h2 className="set-section__title">Grown-up PIN</h2>
      <p className="set-section__lead">
        Guards the Grown-Ups' Corner so little hands don't wander in. Set one here — enter it again
        to change it.
      </p>
      <div className="set-pinrow">
        <Field label="New PIN" htmlFor="set-parent-pin">
          <PinInput id="set-parent-pin" value={pin} onChange={setPin} />
        </Field>
        <Field label="Confirm PIN" htmlFor="set-parent-pin2">
          <PinInput id="set-parent-pin2" value={confirm} onChange={setConfirm} />
        </Field>
      </div>
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}
      <div className="set-actions">
        <Button onClick={save} disabled={busy}>
          {busy ? <Spinner size={18} /> : "Save PIN"}
        </Button>
      </div>
      <Note tone="neutral">
        Clearing the grown-up PIN isn't available in the beta — set a new one here to change it.
      </Note>
    </Card>
  );
}

// ── one child's PIN ─────────────────────────────────────────────────────────────
function ChildPinRow({ child, name }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [open, setOpen] = useState(false);

  async function setChildPin() {
    if (!isPin(pin)) return setMsg({ tone: "danger", text: "PIN must be exactly 4 digits." });
    setBusy(true);
    setMsg(null);
    try {
      await callSetChildPin({ childId: child.id, pin });
      setPin("");
      setOpen(false);
      setMsg({ tone: "ok", text: "PIN set." });
    } catch (e) {
      setMsg({ tone: "danger", text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  async function clearChildPin() {
    setBusy(true);
    setMsg(null);
    try {
      await callClearChildPin({ childId: child.id });
      setMsg({ tone: "ok", text: "PIN removed." });
    } catch (e) {
      setMsg({ tone: "danger", text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="set-childrow">
      <Avatar id={child.avatarId} size={40} />
      <div className="set-childrow__body">
        <div className="set-childrow__name">
          {name || "Unnamed"}
          {child.hasPin ? (
            <Chip tone="ok">
              <span aria-hidden="true">🔒</span> PIN set
            </Chip>
          ) : (
            <Chip tone="neutral">No PIN</Chip>
          )}
        </div>
        <div className="set-childrow__meta">Year {yearLabel(child.yearLevel)}</div>
        {open && (
          <div className="set-pinrow" style={{ marginTop: ".5rem" }}>
            <Field label={child.hasPin ? "New PIN" : "Set a 4-digit PIN"} htmlFor={`cpin-${child.id}`}>
              <PinInput id={`cpin-${child.id}`} value={pin} onChange={setPin} />
            </Field>
            <div className="set-actions" style={{ alignSelf: "end" }}>
              <Button size="sm" onClick={setChildPin} disabled={busy}>
                {busy ? <Spinner size={16} /> : "Save"}
              </Button>
            </div>
          </div>
        )}
        {msg && <Note tone={msg.tone}>{msg.text}</Note>}
      </div>
      <div className="set-childrow__actions">
        <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)} disabled={busy}>
          {child.hasPin ? "Change PIN" : open ? "Close" : "Set PIN"}
        </Button>
        {child.hasPin && (
          <Button size="sm" variant="ghost" onClick={clearChildPin} disabled={busy}>
            Clear
          </Button>
        )}
      </div>
    </li>
  );
}

function ChildPinsSection() {
  const { profiles, displayNameFor } = useProfiles();
  return (
    <Card className="set-section">
      <h2 className="set-section__title">Child PINs</h2>
      <p className="set-section__lead">
        Optional — a PIN keeps a child's own space to themselves when several children share the app.
      </p>
      {profiles.length === 0 ? (
        <Note tone="neutral">Add a child first to give them a PIN.</Note>
      ) : (
        <ul className="set-childlist">
          {profiles.map((c) => (
            <ChildPinRow key={c.id} child={c} name={displayNameFor(c)} />
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── what we've done at home (dates only, NO scores) ─────────────────────────────
function HomeRecord() {
  const { user } = useAuth();
  const { profiles, displayNameFor } = useProfiles();
  const [byChild, setByChild] = useState({});

  useEffect(() => {
    if (!user) return;
    const unsubs = profiles.map((c) =>
      subscribeCompletions(user.uid, c.id, (rows) =>
        setByChild((prev) => ({ ...prev, [c.id]: rows }))
      )
    );
    return () => unsubs.forEach((u) => u && u());
  }, [user, profiles]);

  const items = useMemo(() => {
    const out = [];
    for (const c of profiles) {
      const name = displayNameFor(c);
      for (const comp of byChild[c.id] || []) {
        const d = comp.completedAt && comp.completedAt.toDate ? comp.completedAt.toDate() : null;
        out.push({ key: c.id + "/" + comp.id, name, date: d });
      }
    }
    out.sort((a, b) => (b.date ? b.date.getTime() : 0) - (a.date ? a.date.getTime() : 0));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, byChild]);

  return (
    <Card className="set-section">
      <h2 className="set-section__title">What we've done at home</h2>
      <p className="set-section__lead">
        A private record of the Home Missions you've explored together — dates only, no scores.
      </p>
      {items.length === 0 ? (
        <EmptyState
          icon="🌱"
          title="Nothing recorded yet"
          hint="As you finish Home Missions together, they'll appear here."
        />
      ) : (
        <ul className="set-record">
          {items.map((it) => (
            <li key={it.key} className="set-record__row">
              <span className="set-record__date">
                {it.date
                  ? it.date.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
                  : "Just now"}
              </span>
              <span className="set-record__who">{it.name || "A child"} explored a Home Mission</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── account: Apple link, sign out, delete account ───────────────────────────────
function AccountSection() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [appleMsg, setAppleMsg] = useState(null);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [delErr, setDelErr] = useState("");

  async function doLinkApple() {
    setAppleMsg(null);
    try {
      await linkApple();
      setAppleMsg({ tone: "ok", text: "Apple sign-in linked to this account." });
    } catch (e) {
      setAppleMsg({ tone: "danger", text: errText(e) });
    }
  }

  async function doDelete() {
    setDeleting(true);
    setDelErr("");
    try {
      await callDeleteAccount({});
      // The auth record is removed server-side; the auth listener will drop the session.
      nav("/", { replace: true });
    } catch (e) {
      setDelErr(errText(e));
      setDeleting(false);
    }
  }

  const confirmOk = typed.trim().toUpperCase() === "DELETE";

  return (
    <Card className="set-section">
      <h2 className="set-section__title">Account</h2>
      <p className="set-section__lead">
        Signed in as <strong>{user?.email || "your account"}</strong>.
      </p>

      {APPLE_ENABLED && (
        <div className="set-block">
          <Button variant="secondary" onClick={doLinkApple}>
            Link Apple sign-in
          </Button>
          {appleMsg && <Note tone={appleMsg.tone}>{appleMsg.text}</Note>}
        </div>
      )}

      <div className="set-block">
        <Button variant="secondary" onClick={logout}>
          Sign out
        </Button>
      </div>

      <div className="set-danger">
        <h3 className="set-danger__title">Delete account</h3>
        <Note tone="danger">
          Permanently deletes your account and every child profile, record, dataset and photo. This
          can't be undone.
        </Note>
        <Field label="Type “DELETE” to confirm" htmlFor="set-del-confirm">
          <input
            id="set-del-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            placeholder="DELETE"
          />
        </Field>
        {delErr && <Note tone="danger">{delErr}</Note>}
        <div className="set-actions">
          <Button variant="danger" onClick={doDelete} disabled={!confirmOk || deleting}>
            {deleting ? <Spinner size={18} /> : "Delete my account"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function Settings() {
  return (
    <main className="container">
      <PageHeader
        title="Settings"
        subtitle="PINs, account and your private home record."
        back="/grown-ups"
      />
      <AppearanceSection />
      <VoiceSection />
      <ParentPinSection />
      <ChildPinsSection />
      <HomeRecord />
      <AccountSection />

      <footer className="set-footer">Independently made — not affiliated with or endorsed by ACARA.</footer>
    </main>
  );
}

injectStyles(
  "maths-settings-styles",
  `
.set-section{margin:0 0 1.1rem;}
.set-section__title{margin:0 0 .3rem;font-size:1.15rem;}
.set-section__lead{margin:0 0 .85rem;color:var(--ink-soft);}
.set-theme{display:flex;gap:.5rem;flex-wrap:wrap;}
.set-pinrow{display:flex;gap:1rem;flex-wrap:wrap;align-items:flex-start;}
.set-actions{display:flex;gap:.6rem;flex-wrap:wrap;margin-top:.35rem;}
.set-block{margin:0 0 .9rem;}
.set-childlist{list-style:none;margin:0;padding:0;display:grid;gap:.6rem;}
.set-childrow{display:flex;align-items:flex-start;gap:.8rem;padding:.7rem 0;border-top:1px solid var(--line);}
.set-childrow:first-child{border-top:none;}
.set-childrow__body{flex:1 1 10rem;min-width:0;}
.set-childrow__name{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;font-weight:700;color:var(--brand-ink);}
.set-childrow__meta{color:var(--ink-soft);font-size:.88rem;margin-top:.12rem;}
.set-childrow__actions{display:flex;gap:.4rem;flex-wrap:wrap;justify-content:flex-end;flex:0 0 auto;}
.set-record{list-style:none;margin:0;padding:0;display:grid;gap:.4rem;}
.set-record__row{display:flex;gap:.75rem;align-items:baseline;flex-wrap:wrap;padding:.4rem 0;border-top:1px solid var(--line);}
.set-record__row:first-child{border-top:none;}
.set-record__date{font-weight:700;color:var(--brand-ink);min-width:8.5rem;}
.set-record__who{color:var(--ink-soft);}
.set-danger{margin-top:1rem;padding-top:1rem;border-top:1px solid var(--line);}
.set-danger__title{margin:0 0 .5rem;color:var(--danger);font-size:1rem;}
.set-footer{margin:2rem 0 1rem;text-align:center;color:var(--ink-soft);font-size:.82rem;}
@media (max-width:34rem){
  .set-childrow{flex-wrap:wrap;}
  .set-childrow__actions{width:100%;justify-content:flex-start;}
}
`
);
